import type { CopyShape } from "./copy.types";
import { DEFAULT_LOCALE, getLocale, type Locale } from "./locale";

type Translations<T> = { readonly [L in Exclude<Locale, typeof DEFAULT_LOCALE>]: CopyShape<T> };

type Node = Record<PropertyKey, unknown>;

function isBranch(value: unknown): value is Node {
  return typeof value === "object" && value !== null;
}

function nodeAt(root: unknown, path: readonly PropertyKey[]): Node {
  let node = root;
  for (const key of path) node = (node as Node)[key];
  return node as Node;
}

/**
 * Junta o copy em português com as traduções e devolve um objeto com o mesmo
 * formato, que responde no idioma de agora (`getLocale()`) a cada leitura.
 *
 * Por isso as telas continuam lendo `X_COPY.algo` como sempre: nenhuma muda.
 * O que muda é a regra de quem lê — **ler no render, nunca no escopo do
 * módulo**. `const COPY = X_COPY.editor` no topo do arquivo é permitido
 * (o sub-objeto continua vivo); `const TITLE = X_COPY.title` não: a string
 * congela no idioma do momento do import.
 *
 * O tipo devolvido é o do português, literais incluídos, para os consumidores
 * que já existem compilarem sem mudança.
 */
export function defineCopy<const T extends object>(source: T, translations: Translations<T>): T {
  const catalogs: Record<Locale, unknown> = { [DEFAULT_LOCALE]: source, ...translations } as Record<Locale, unknown>;
  const cache = new Map<string, object>();

  function current(path: readonly PropertyKey[]): Node {
    return nodeAt(catalogs[getLocale()] ?? source, path);
  }

  function proxyAt(path: readonly PropertyKey[]): object {
    const cacheKey = path.map(String).join("\u0000");
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    // O alvo é o nó em português: dá a forma (objeto ou array) que
    // `Array.isArray`, spread e `JSON.stringify` enxergam. O conteúdo vem
    // sempre do idioma de agora.
    const shape = nodeAt(source, path);
    const proxy = new Proxy(shape, {
      get(_target, key) {
        const value = Reflect.get(current(path), key);
        if (isBranch(value) && typeof key !== "symbol") return proxyAt([...path, key]);
        return value;
      },
      has(_target, key) {
        return Reflect.has(current(path), key);
      },
      ownKeys() {
        return Reflect.ownKeys(current(path));
      },
      getOwnPropertyDescriptor(_target, key) {
        const node = current(path);
        const descriptor = Reflect.getOwnPropertyDescriptor(node, key);
        if (!descriptor) return undefined;
        // Texto que depende da formatação (`get amount()` com o símbolo da
        // moeda) é getter: relata como dado, já lido, senão spread e
        // `Object.keys` quebram com "accessors and a value".
        if (descriptor.get || descriptor.set) {
          const { enumerable, configurable } = descriptor;
          return { value: Reflect.get(node, key), writable: false, enumerable, configurable };
        }
        const value = isBranch(descriptor.value) && typeof key !== "symbol" ? proxyAt([...path, key]) : descriptor.value;
        // Invariante do Proxy: propriedade não configurável no alvo (o
        // `length` de um array) tem de ser relatada igual. O resto, sim.
        const configurable = Reflect.getOwnPropertyDescriptor(shape, key)?.configurable ?? true;
        return { ...descriptor, value, configurable };
      },
    });
    cache.set(cacheKey, proxy);
    return proxy;
  }

  return proxyAt([]) as T;
}
